import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { AI_BULK_SEND_OUTREACH, AI_BULK_DECLINE, AI_UNDO_ACTION } from './gql';
import AssignRecruiterModal, { JobOrderTarget } from './AssignRecruiterModal';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------
export interface BulkActionBarProps {
  jobOrderId: string;
  selectedIds: string[];
  onCleared: () => void;
  onActionDone: (actedIds: string[]) => void;
}

// ---------------------------------------------------------------------------
// Undo toast content
// ---------------------------------------------------------------------------

interface UndoToastProps {
  label: string;
  affectedIds: string[];
  onUndo: (ids: string[]) => void;
  toastId: string | number;
}

const UndoToastContent: FC<UndoToastProps> = ({ label, affectedIds, onUndo, toastId }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
    <span>{label}</span>
    <Button
      size="sm"
      color="link"
      style={{ padding: '0 4px', whiteSpace: 'nowrap', color: '#fff', textDecoration: 'underline' }}
      onClick={() => {
        toast.dismiss(toastId);
        onUndo(affectedIds);
      }}
    >
      Undo
    </Button>
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BulkActionBar: FC<BulkActionBarProps> = ({
  jobOrderId,
  selectedIds,
  onCleared,
  onActionDone,
}) => {
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const [bulkSendOutreach, { loading: sendingOutreach }] = useMutation(AI_BULK_SEND_OUTREACH);
  const [bulkDecline, { loading: declining }] = useMutation(AI_BULK_DECLINE);
  const [undoAction] = useMutation(AI_UNDO_ACTION);

  const isMutating = sendingOutreach || declining;

  // ---- Undo handler — called when the Undo button in the toast is clicked ----
  const handleUndo = (ids: string[]) => {
    Promise.all(
      ids.map((pipelineEntryId) =>
        undoAction({ variables: { pipelineEntryId } }).catch((err: Error) => {
          toast.error(`Undo failed for entry ${pipelineEntryId}: ${err.message}`);
        })
      )
    ).then(() => {
      // Undo restores rows — pass empty actedIds so nothing is optimistically hidden
      onActionDone([]);
    });
  };

  // ---- Send outreach ----
  const handleSendOutreach = async () => {
    const ids = [...selectedIds];
    try {
      await bulkSendOutreach({
        variables: { input: { pipelineEntryIds: ids } },
      });
      const toastId = toast.success(
        <UndoToastContent
          label={`${ids.length} applicant${ids.length !== 1 ? 's' : ''} marked for outreach.`}
          affectedIds={ids}
          onUndo={handleUndo}
          toastId=""
        />,
        { autoClose: 8000 }
      );
      // Re-render toast with its real id so the Undo button can dismiss it
      toast.update(toastId, {
        render: (
          <UndoToastContent
            label={`${ids.length} applicant${ids.length !== 1 ? 's' : ''} marked for outreach.`}
            affectedIds={ids}
            onUndo={handleUndo}
            toastId={toastId}
          />
        ),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Send outreach failed: ${message}`);
    } finally {
      // Always refresh + clear: the DB transition commits even if the mutation's
      // response serialization fails, so refetch so the new status is reflected.
      onActionDone(ids);
    }
  };

  // ---- Decline ----
  const handleDecline = async () => {
    const ids = [...selectedIds];
    try {
      await bulkDecline({
        variables: { input: { pipelineEntryIds: ids } },
      });
      const toastId = toast.success(
        <UndoToastContent
          label={`${ids.length} applicant${ids.length !== 1 ? 's' : ''} declined.`}
          affectedIds={ids}
          onUndo={handleUndo}
          toastId=""
        />,
        { autoClose: 8000 }
      );
      toast.update(toastId, {
        render: (
          <UndoToastContent
            label={`${ids.length} applicant${ids.length !== 1 ? 's' : ''} declined.`}
            affectedIds={ids}
            onUndo={handleUndo}
            toastId={toastId}
          />
        ),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Decline failed: ${message}`);
    } finally {
      onActionDone(ids);
    }
  };

  // ---- Assign modal ----
  const jobOrderTarget: JobOrderTarget = { jobOrderId, jobTitle: null };

  const handleAssigned = () => {
    setAssignModalOpen(false);
    onActionDone([...selectedIds]);
    onCleared();
  };

  return (
    <>
      <div className="ai-console__bulk-bar">
        <span className="ai-console__bulk-count">
          {selectedIds.length} selected
        </span>
        <span className="ai-console__bulk-hint">
          actions apply within this job order only
        </span>

        <div className="ai-console__bulk-actions">
          <Button
            size="sm"
            color="warning"
            disabled={isMutating || selectedIds.length === 0}
            onClick={handleSendOutreach}
          >
            Send outreach
          </Button>

          <Button
            size="sm"
            color="secondary"
            outline
            disabled={isMutating || selectedIds.length === 0}
            onClick={() => setAssignModalOpen(true)}
          >
            Assign to recruiter
          </Button>

          <Button
            size="sm"
            color="danger"
            outline
            disabled={isMutating || selectedIds.length === 0}
            onClick={handleDecline}
          >
            Decline
          </Button>

          <Button
            size="sm"
            color="link"
            disabled={isMutating}
            onClick={onCleared}
          >
            Clear
          </Button>
        </div>
      </div>

      <AssignRecruiterModal
        isOpen={assignModalOpen}
        jobOrderTarget={jobOrderTarget}
        selectedIds={selectedIds}
        onClose={() => setAssignModalOpen(false)}
        onAssigned={handleAssigned}
      />
    </>
  );
};

export default BulkActionBar;
